import * as Sentry from '@sentry/nextjs';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { removeLocale } from 'queries/keys.utils';
import { StaleTime } from 'queries/types';
import { TICKETS_PREFIX } from 'restful/tickets/constants';
import { useTicketingCartId, useTicketingEventId } from 'restful/tickets/utils';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import type { Cart } from 'services/api/restful/generated/nofomoRestApi.schemas';
import { getCart } from 'services/api/restful/generated/tickets/tickets';

export type TicketsCartQueryData = { cart?: Cart };

export const ticketsCartQuery = (variables: {
  cartId: string;
  eventId: string;
}) =>
  queryOptions<TicketsCartQueryData, RestApiError>({
    queryKey: [TICKETS_PREFIX, 'cart', removeLocale(variables)],
    queryFn: async ({ signal }) => {
      const { data } = await restCall(() =>
        getCart(variables.eventId, variables.cartId, { signal }),
      );

      return { cart: data };
    },
  });

export const useTicketsCart = () => {
  const { cartId, setCartId } = useTicketingCartId();
  const { eventId } = useTicketingEventId();
  const enabled = !!cartId && !!eventId;

  const { data, isLoading, isFetching, isPending, isPaused, status, error } =
    useQuery({
      ...ticketsCartQuery({ cartId, eventId }),
      queryFn: async ({ signal }) => {
        try {
          const { data: cartData } = await restCall(() =>
            getCart(eventId, cartId, { signal }),
          );

          return { cart: cartData };
        } catch (fetchError) {
          if (fetchError instanceof RestApiError && fetchError.isNotFound) {
            setCartId('');
          } else {
            Sentry.captureException(fetchError);
          }
          throw fetchError;
        }
      },
      staleTime: StaleTime.THIRTY_SEC,
      retry: false,
      refetchOnWindowFocus: 'always',
      enabled,
    });

  return {
    cart: data?.cart,
    isLoading: (isPending && !isPaused && enabled) || (isLoading && isFetching),
    status,
    error: error instanceof RestApiError ? error : undefined,
  };
};
